import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import FormInput from 'components/forms/FormInput';
import FormRadio from 'components/forms/FormRadio';
import { ROLE_ROOT_EMPLOYEE, ROLE_SUPERADMIN } from 'js/constants';
import may from 'js/security';

const OPTION_USER = 'user';
const OPTION_COMPANY = 'company';
const OPTION_COMPANY_DETAILED_ROLE = 'companyDetailedRole';
const OPTION_ANCESTOR = 'ancestor';

function UserSubscriptionPaidBy(props) {
  const { inModal, entity, disabled } = props;

  // const username = entity.getUsername();
  const isUserSubscriptionPaidByCompany = entity.getIsUserSubscriptionPaidByCompany();
  const isUserSubscriptionPaidByCompanyDetailedRole = entity.getIsUserSubscriptionPaidByCompanyDetailedRole();
  const isUserSubscriptionPaidByUser = entity.getIsUserSubscriptionPaidByUser();

  const selectedPayer = isUserSubscriptionPaidByCompany
    ? OPTION_COMPANY
    : isUserSubscriptionPaidByCompanyDetailedRole
    ? OPTION_COMPANY_DETAILED_ROLE
    : isUserSubscriptionPaidByUser
    ? OPTION_USER
    : OPTION_ANCESTOR;

  const feeForRoleUser = entity.getFeeForRoleUser();
  const feeForRoleAdmin = entity.getFeeForRoleAdmin();
  const feeForRoleWlAdmin = entity.getFeeForRoleWlAdmin();

  /**
   * Find first company with isDebtor === true
   * @param {Object} entity
   * @returns Object|null
   */
  const lookupDebtor = entity => {
    let sanityCheck = 100; // that deep structure should never be the case
    let client = entity.data;
    while (client?.username && --sanityCheck) {
      const isDebtor = client?.financial?.is_debtor;
      if (isDebtor) {
        return client;
      }
      client = client?.parent;
      if (!client) {
        return null;
      }
    }
  };

  const debtor = useMemo(() => lookupDebtor(entity), [entity?.getUsername?.()]);

  const { messages } = useIntl();
  const { subscription_automation: msg } = messages;

  const options = [
    { label: messages.holding_structure, value: OPTION_ANCESTOR },
    { label: messages.subscription_companies, value: OPTION_COMPANY },
    { label: messages.subscription_companies_detailed_role, value: OPTION_COMPANY_DETAILED_ROLE },
    { label: messages.subscription_clients, value: OPTION_USER },
  ];

  const updateEntity = (propertyName, value) => {
    const { entity, changeEntity } = props;
    const newEntity = { ...entity.updateValue(propertyName, value) };
    changeEntity(newEntity);
  };

  const updatePayer = (_, value) => {
    const { entity, changeEntity } = props;

    const updatedIsUserSubscriptionPaidByCompany = value === OPTION_COMPANY;
    const updatedIsUserSubscriptionPaidByCompanyDetailedRole = value === OPTION_COMPANY_DETAILED_ROLE;
    const updatedIsUserSubscriptionPaidByUser = value === OPTION_USER;

    const changeSet = [
      ['isUserSubscriptionPaidByCompany', updatedIsUserSubscriptionPaidByCompany],
      ['isUserSubscriptionPaidByCompanyDetailedRole', updatedIsUserSubscriptionPaidByCompanyDetailedRole],
      ['isUserSubscriptionPaidByUser', updatedIsUserSubscriptionPaidByUser],
    ];
    const newEntity = changeSet.reduce((acc, changes) => ({ ...acc.updateValue(...changes) }), entity);
    changeEntity(newEntity);
  };

  // Security
  const user = useSelector(({ login }) => login.user);
  const isAccessGranted = [ROLE_ROOT_EMPLOYEE, ROLE_SUPERADMIN].some(role => may(role, user.roles));
  if (!isAccessGranted) {
    return;
  }

  const billingTargetName = debtor?.profile?.company ?? '---';
  const debtorInfo = debtor ? (
    <div>{msg.debtor_ancestor.replace('%billingTargetName%', billingTargetName)}</div>
  ) : (
    <div>{msg.debtor_ancestor_missing}</div>
  );

  // Hint to create a Company Subscription
  const companySubscriptionHint = (
    <>
      <h2>{msg.hint_title}</h2>
      <p>{msg.hint_body}</p>
    </>
  );

  const feeInputs = (
    <>
      <FormInput
        name="feeForRoleUser"
        label={msg.fee_for_role_user}
        value={feeForRoleUser}
        onChange={updateEntity}
        disabled={disabled}
        type="number"
      />
      <FormInput
        name="feeForRoleAdmin"
        label={msg.fee_for_role_admin}
        value={feeForRoleAdmin}
        onChange={updateEntity}
        disabled={disabled}
        type="number"
      />
      <FormInput
        name="feeForRoleWlAdmin"
        label={msg.fee_for_role_wl_admin}
        value={feeForRoleWlAdmin}
        onChange={updateEntity}
        disabled={disabled}
        type="number"
      />
    </>
  );

  return (
    <Row>
      <Col xs={12} md={12}>
        <div className="panel panel-transparent">
          <div className="panel-body">
            <h4>{msg.title}</h4>
            <div className="form-group-attached">
              <FormRadio
                name="payer"
                label={msg.payer}
                value={selectedPayer}
                onChange={updatePayer}
                disabled={disabled}
                options={options}
              />

              {selectedPayer === OPTION_COMPANY && companySubscriptionHint}
              {selectedPayer === OPTION_COMPANY_DETAILED_ROLE && feeInputs}
              {selectedPayer === OPTION_USER && feeInputs}
              {selectedPayer === OPTION_ANCESTOR && debtorInfo}
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
}

export default UserSubscriptionPaidBy;
